import { useEffect, useState } from 'react';
import { useDebouncedEffect } from './debounce';
import { CommonConstant } from '../utility/constants';
import { BillingService, PackagingMaterialService } from '../utility/services';

export const PackagingMaterialListHook = warehouseId => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState({
    limit: CommonConstant.defaultPageSize,
    page: 1,
    search: '',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const temp = {
        ...filter,
        warehouseId,
      };
      if (temp.search) {
        temp.search = temp.search.trim();
      }
      const res = await PackagingMaterialService.get(temp);
      setData(res?.data);
      setTotalData(res?.total);
      setTotalPages(res?.totalPages);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      if (warehouseId) {
        fetchData();
      }
    },
    [filter, refreshKey, warehouseId],
    500,
  );

  const pageChanged = page => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  const setSearch = search => {
    filterChanged('search', search);
  };

  const refresh = () => {
    setRefreshKey(Math.random());
  };

  const updateAllFilter = obj => {
    setFilter(filter => ({
      ...filter,
      page: 1,
      ...obj,
    }));
  };

  return {
    data,
    error,
    filter,
    filterChanged,
    loading,
    pageChanged,
    setSearch,
    totalData,
    totalPages,
    refresh,
    updateAllFilter,
    setData,
  };
};

export const packagingAutocompleteHook = (warehouseId, search, status) => {
  const [data, setData] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const temp = {
        search,
        warehouseId,
        limit: 50,
      };

      if (status) {
        temp.status = status;
      }

      if (temp.search) {
        temp.search = temp.search.trim();
      }
      const result = await PackagingMaterialService.get(temp);
      const mapped = result.data.map(d => ({ ...d, search }));
      setData(mapped);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setData([]);
    fetchData();
  }, [search, warehouseId]);

  return { data, error, loading };
};

export const PackagingMaterialDetailHook = id => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await PackagingMaterialService.getById(id);
      setData(res || {});
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  return { data, loading, error, refresh: fetchData, setData };
};

export const PackagingMaterialMetadataHook = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await BillingService.metadata();
      setData(res || {});
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return { data, loading, error, refresh: fetchData };
};
